import { type ReactNode, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { endpoints } from './api/endpoints';
import { refreshSession } from './api/http';
import { GameLayout } from './components/GameLayout';
import { Spinner } from './components/ui';
import { AllianceScreen } from './screens/alliance';
import { AllianceProfileScreen } from './screens/allianceProfile';
import {
  CheckEmailScreen,
  ForgotPasswordScreen,
  LoginScreen,
  RegisterScreen,
  ResetPasswordScreen,
  VerifyEmailScreen,
} from './screens/auth';
import { BattleReportScreen, BattleScreen, BattlesScreen } from './screens/battles';
import { CodexScreen } from './screens/codex';
import { DispatchScreen } from './screens/dispatch';
import { EmpireScreen } from './screens/empire';
import { FleetScreen } from './screens/fleet';
import { GalaxiesScreen } from './screens/GalaxiesScreen';
import { MenuScreen, SystemScreen, SystemsScreen } from './screens/game';
import { InboxScreen } from './screens/inbox';
import { IntelScreen } from './screens/intel';
import { GalaxyMapScreen } from './screens/map';
import { PlayerScreen } from './screens/player';
import { RankingsScreen } from './screens/rankings';
import { ReinforceScreen } from './screens/reinforce';
import { WelcomeScreen } from './screens/welcome';
import { useSession } from './state/session';

/** Restores a session from the refresh cookie once per page load. */
function useSessionBootstrap() {
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const session = useSession.getState();
      if (session.status !== 'unknown') return;
      if (await refreshSession()) {
        try {
          const me = await endpoints.me();
          const token = useSession.getState().accessToken;
          if (!cancelled && token) useSession.getState().signIn(token, me.account);
          return;
        } catch {
          // fall through to anonymous
        }
      }
      if (!cancelled) useSession.getState().clear();
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  const location = useLocation();
  if (status === 'unknown') return <Spinner label="Connecting…" />;
  if (status === 'anonymous') {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

/** Signed-in visitors leave for `to`; /register sends them to "Check your email" (P23). */
function PublicOnly({ children, to = '/galaxies' }: { children: ReactNode; to?: string }) {
  const status = useSession((s) => s.status);
  if (status === 'unknown') return <Spinner label="Connecting…" />;
  return status === 'authenticated' ? <Navigate to={to} replace /> : children;
}

export function App() {
  useSessionBootstrap();
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicOnly>
            <Navigate to="/welcome" replace />
          </PublicOnly>
        }
      />
      <Route path="/welcome" element={<WelcomeScreen />} />
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginScreen />
          </PublicOnly>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnly to="/check-email">
            <RegisterScreen />
          </PublicOnly>
        }
      />
      <Route
        path="/check-email"
        element={
          <RequireAuth>
            <CheckEmailScreen />
          </RequireAuth>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/verify-email" element={<VerifyEmailScreen />} />
      <Route
        path="/galaxies"
        element={
          <RequireAuth>
            <GalaxiesScreen />
          </RequireAuth>
        }
      />
      <Route
        path="/g/:galaxyId"
        element={
          <RequireAuth>
            <GameLayout />
          </RequireAuth>
        }
      >
        <Route index element={<GalaxyMapScreen />} />
        <Route path="systems" element={<SystemsScreen />} />
        <Route path="systems/:systemId" element={<SystemScreen />} />
        <Route path="fleet" element={<FleetScreen />} />
        <Route path="fleet/send" element={<DispatchScreen />} />
        <Route path="fleet/reinforce" element={<ReinforceScreen />} />
        <Route path="battles" element={<BattlesScreen />} />
        <Route path="battles/:battleId" element={<BattleScreen />} />
        <Route path="battle-reports/:battleId" element={<BattleReportScreen />} />
        <Route path="intel" element={<IntelScreen />} />
        <Route path="alliance" element={<AllianceScreen />} />
        <Route path="alliances/:allianceId" element={<AllianceProfileScreen />} />
        <Route path="players/:playerId" element={<PlayerScreen />} />
        <Route path="empire" element={<EmpireScreen />} />
        <Route path="rankings" element={<RankingsScreen />} />
        <Route path="inbox" element={<InboxScreen />} />
        <Route path="codex" element={<CodexScreen />} />
        <Route path="menu" element={<MenuScreen />} />
      </Route>
      <Route path="*" element={<Navigate to="/galaxies" replace />} />
    </Routes>
  );
}
